import { Alert, Button, DatePicker, Dropdown, Form, Input, Modal, Space } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FaCheck, FaRegCopy } from 'react-icons/fa'
import { GoUpload } from 'react-icons/go'
import { IoMdMore } from 'react-icons/io'
import { MdOutlineForum, MdOutlineReportProblem } from 'react-icons/md'
import { RxUpdate } from 'react-icons/rx'

import { openEntityChat } from '@/Containers/CommunicationSection/ChatContainer/openEntityChat'
import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'

import { APPLICATION_TEXTS as T } from '../Contracts/ApplicationFieldLabels'
import { APPLICATION_RESOURCE } from '../Contracts/IApplication'
import {
    APPLICATION_STRING_MAX_LENGTH,
    APPLICATION_TEXT_MAX_LENGTH,
    applicationActionsStoreFor,
} from '../Stores/ApplicationActionsStore'
import { CopyApplicationTask } from '../Tasks/CopyApplicationTask'

import type { ApplicationCrudContextLike } from '../Contracts/IApplication'
import type { ApplicationActionsStore, ApplicationModal } from '../Stores/ApplicationActionsStore'
import type { MenuProps } from 'antd'
import type { ReactNode } from 'react'

/** Параметри обробника `detailConfig.modeConfig.view.onButtons` ядра. */
export interface ApplicationButtonsParams {
    buttons: Record<string, unknown>
    crudContext?: unknown
    content?: unknown[]
}

interface ButtonDeps {
    formContext?: { isEditable?: boolean | undefined } | undefined
}

/**
 * Що кнопки беруть із картки — у звичайному (не MobX) класі: ядрова
 * `ButtonGroup` кладе `children` у глибоко спостережуваний стор і віддає
 * назад через `toJS`, і спостережуваний стор у пропсах повернувся б копією без
 * методів (той самий прийом, що в кнопках переходів запиту).
 */
class ActionsSource {
    constructor(
        readonly store: ApplicationActionsStore,
        readonly deps: ButtonDeps
    ) {}
}

/** Пункти «⋮» (`SPEC-021` §8.6) — кожен за своїм `_permissions`. */
const MENU: { modal: ApplicationModal; permission: 'report_problem' | 'resolve_problem' | 'unload'; label: string; icon: ReactNode }[] = [
    { modal: 'reportProblem', permission: 'report_problem', label: T.reportProblem, icon: <MdOutlineReportProblem /> },
    { modal: 'resolveProblem', permission: 'resolve_problem', label: T.resolveProblem, icon: <FaCheck /> },
    { modal: 'unload', permission: 'unload', label: T.unload, icon: <GoUpload /> },
]

const canCopy = (): boolean => CheckGlobalResourceAccessTask(APPLICATION_RESOURCE, 'create')

/** «Оновити статус» і «Копіювати» — у шапці; лише в перегляді. */
const HeaderButtons = observer(({ source }: { source: ActionsSource }) => {
    const { store, deps } = source

    if (deps.formContext?.isEditable === true) return null

    const canUpdate = store.permissions.update === true

    return (
        <Space size={8}>
            {canUpdate ? (
                <Button className="button-group-item" size="middle" icon={<RxUpdate />} onClick={() => store.open('statusClient')}>
                    {T.updateStatus}
                </Button>
            ) : null}
            {canCopy() ? (
                <Button
                    className="button-group-item"
                    size="middle"
                    icon={<FaRegCopy />}
                    onClick={() => CopyApplicationTask(store.values, () => store.reloadList())}
                >
                    {T.copy}
                </Button>
            ) : null}
        </Space>
    )
})

/**
 * «⋮» з діями заявки — власний antd-`Dropdown` на antd-кнопці, як у
 * верифікації CRM: ядровий «⋮» (`priority: 'dropdown'`) перемонтовує тригер
 * на кожен рендер, і меню висить за межами екрана.
 */
const ActionsMenu = observer(({ source }: { source: ActionsSource }) => {
    const { store, deps } = source

    if (deps.formContext?.isEditable === true) return null

    const permissions = store.permissions
    const items: MenuProps['items'] = MENU.filter(({ permission }) => permissions[permission] === true).map(
        ({ modal, label, icon }) => ({ key: modal, label, icon, onClick: () => store.open(modal) })
    )

    if (items.length === 0) return null

    return (
        <Dropdown menu={{ items }} placement="bottomRight">
            <Button
                icon={<IoMdMore style={{ fontSize: 22 }} />}
                className="button-group-item"
                size="middle"
                aria-label={T.actionsMenu}
            />
        </Dropdown>
    )
})

const ModalShell = observer(
    ({ store, modal, title, children }: { store: ApplicationActionsStore; modal: ApplicationModal; title: string; children: ReactNode }) => (
        <Modal
            title={title}
            open={store.modal === modal}
            okText={T.save}
            cancelText={T.close}
            okButtonProps={{ loading: store.pending }}
            onOk={() => void store.submit()}
            onCancel={() => store.close()}
            destroyOnHidden
            maskClosable={false}
        >
            {store.error ? <Alert type="error" showIcon title={store.error} style={{ marginBottom: 16 }} /> : null}
            <Form layout="vertical" component="div">
                <Form.Item
                    required
                    validateStatus={store.fieldError ? 'error' : ''}
                    help={store.fieldError ?? undefined}
                    style={{ marginBottom: 0 }}
                >
                    {children}
                </Form.Item>
            </Form>
        </Modal>
    )
)

/**
 * Модалки кнопок (`SPEC-021` §8.6): одне поле без підпису — заголовок модалки
 * і є підписом, як у легасі (`useButtons4DrawerExtra.jsx:20-96`).
 */
const ApplicationModals = observer(({ store }: { store: ApplicationActionsStore }) => (
    <>
        <ModalShell store={store} modal="statusClient" title={T.updateStatusTitle}>
            <Input
                value={store.text}
                maxLength={APPLICATION_STRING_MAX_LENGTH}
                onChange={(event) => store.setText(event.target.value)}
                aria-label={T.updateStatusTitle}
            />
        </ModalShell>
        <ModalShell store={store} modal="reportProblem" title={T.reportProblemTitle}>
            <Input.TextArea
                rows={4}
                value={store.text}
                maxLength={APPLICATION_TEXT_MAX_LENGTH}
                onChange={(event) => store.setText(event.target.value)}
                aria-label={T.reportProblemTitle}
            />
        </ModalShell>
        <ModalShell store={store} modal="resolveProblem" title={T.resolveProblemTitle}>
            <Input.TextArea
                rows={4}
                value={store.text}
                maxLength={APPLICATION_TEXT_MAX_LENGTH}
                onChange={(event) => store.setText(event.target.value)}
                aria-label={T.resolveProblemTitle}
            />
        </ModalShell>
        <ModalShell store={store} modal="unload" title={T.unloadTitle}>
            <DatePicker
                format="DD.MM.YYYY"
                value={store.date ? dayjs(store.date) : null}
                onChange={(value) => store.setDate(value ? value.format('YYYY-MM-DD') : null)}
                style={{ width: '100%' }}
                aria-label={T.unloadTitle}
            />
        </ModalShell>
    </>
))

/**
 * Кнопки картки заявки (`SPEC-021` §8.6): «Оновити статус», «Копіювати» у
 * шапці, «Проблемне замовлення», «Проблема вирішена», «Вивантажився» в «⋮».
 * «Програти» з його модалкою — біля смуги статусів (`ApplicationStatusBar`).
 *
 * Підключення в ресурсі: `detailConfig.modeConfig.view.onButtons = addApplicationButtons`.
 */
export const addApplicationButtons = ({ buttons, crudContext, content }: ApplicationButtonsParams): void => {
    if (crudContext === null || typeof crudContext !== 'object') return

    const context = crudContext as ApplicationCrudContextLike
    const store = applicationActionsStoreFor(context)
    const depStore = { formContext: context.formContext }
    const source = new ActionsSource(store, depStore)

    // Кнопка «Чат» — get-or-create групового чату заявки, перша в ряду.
    // Учасники — відповідальні заявки (менеджер клієнт і логіст);
    // видима кожному, хто бачить картку: право на чат бек перевіряє сам
    // (ApplicationRule::READ у Create/ChatAction). Лоадер — у дровері чату
    buttons['chat'] = {
        label: T.chat,
        icon: <MdOutlineForum />,
        size: 'middle',
        priority: 'button',
        sort: 0,
        depStore,
        depFn: () => store.uuid !== null,
        onClick: () => {
            const uuid = store.uuid
            if (uuid !== null) void openEntityChat('application', uuid)
        },
    }

    // Праворуч від «Редагувати» (sort 1000): спершу кнопки, потім «⋮»
    buttons['applicationHeaderButtons'] = {
        priority: 'button',
        sort: 2000,
        depStore,
        depFn: (deps: ButtonDeps) => deps.formContext?.isEditable !== true,
        children: <HeaderButtons key="application-header-buttons" source={source} />,
    }

    buttons['applicationActionsMenu'] = {
        priority: 'button',
        sort: 2100,
        depStore,
        depFn: (deps: ButtonDeps) => deps.formContext?.isEditable !== true,
        children: <ActionsMenu key="application-actions-menu" source={source} />,
    }

    content?.push(<ApplicationModals key="application-modals" store={store} />)
}
